/* eslint-disable react-refresh/only-export-components */
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from '@/components/toast/toaster';
import { Toaster as SonnerToaster } from 'sonner';
import { ThemeProvider } from '@/components/theme-provider';
import { LanguageProvider } from '@/components/language-provider';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useState, useEffect } from 'react';
import { MsalProvider } from '@azure/msal-react';
import { initializeMsal, getMsalInstance, getIsLoginMicrosoft } from '@/pages/auth/login/authConfig';

import '@/index.css';
import router from '@/router';
import { ErrorBoundary } from 'react-error-boundary';
import MaintenanceError from './pages/errors/maintenance-error';
import * as Sentry from '@sentry/react';
import { VITE_GOOGLE_CLIENT_ID } from './libs/const';

// Environment variables
const ENV = {
  GOOGLE_CLIENT_ID: VITE_GOOGLE_CLIENT_ID,
  USE_SENTRY: import.meta.env.VITE_USE_SENTRY === 'true',
  SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN || 'dsn',
  ENV_NAME: import.meta.env.VITE_ENV || 'development',
  SENTRY_TRACES_SAMPLE_RATE: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE) || 1.0,
  REPLAY_SESSION_SAMPLE_RATE: Number(import.meta.env.VITE_REPLAY_SESSION_SAMPLE_RATE) || 1.0,
  REPLAY_ON_ERROR_SAMPLE_RATE: Number(import.meta.env.VITE_REPLAY_ON_ERROR_SAMPLE_RATE) || 1.0,
  SMARTLOOK_KEY: import.meta.env.VITE_SMARTLOOK_KEY || '',
  ENABLE_SMARTLOOK: import.meta.env.VITE_ENABLE_SMARTLOOK === 'true',
};

// Initialize analytics and monitoring services
function initializeServices() {
  // Initialize Sentry if enabled
  const isLocalEnv = ENV.ENV_NAME?.toLowerCase() === 'local' || import.meta.env.DEV;

  if (ENV.USE_SENTRY && !isLocalEnv) {
    Sentry.init({
      dsn: ENV.SENTRY_DSN,
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
      environment: ENV.ENV_NAME,
      tracesSampleRate: ENV.SENTRY_TRACES_SAMPLE_RATE,
      // Session Replay
      replaysSessionSampleRate: ENV.REPLAY_SESSION_SAMPLE_RATE,
      replaysOnErrorSampleRate: ENV.REPLAY_ON_ERROR_SAMPLE_RATE,
    });
  }

  // Initialize Smartlook if enabled
  if (ENV.ENABLE_SMARTLOOK && ENV.SMARTLOOK_KEY) {
    // Create a script element to load Smartlook
    const script = document.createElement('script');
    script.innerHTML = `
      window.smartlook||(function(d) {
        var o=smartlook=function(){
          if (o.api && Array.isArray(o.api)) {
            o.api.push(arguments);
          }
        },h=d.getElementsByTagName('head')[0];
        var c=d.createElement('script');o.api=new Array();c.async=true;c.type='text/javascript';
        c.charset='utf-8';c.src='https://web-sdk.smartlook.com/recorder.js';h.appendChild(c);
      })(document);
      // Wait for smartlook to be fully initialized before calling init
      setTimeout(function() {
        var smartlookGlobal = window.smartlook;
        if (typeof smartlookGlobal === 'function' && Array.isArray(smartlookGlobal.api)) {
          smartlookGlobal('init', '${ENV.SMARTLOOK_KEY}');
        }
      }, 100);
    `;
    document.head.appendChild(script);
  }
}

// Initialize services on app load
initializeServices();

function App() {
  const [msalInstance, setMsalInstance] = useState<any>(null);

  useEffect(() => {
    const init = async () => {
      try {
        await initializeMsal();
        if (getIsLoginMicrosoft()) {
          setMsalInstance(getMsalInstance());
        }
      } catch (error) {
        // console.error('Failed to initialize MSAL:', error);
        if (ENV.USE_SENTRY) {
          Sentry.captureException(error);
          console.error('Failed to initialize MSAL:', error);
        }
      }
    };
    init();
  }, []);

  return (
    <ErrorBoundary
      fallback={<MaintenanceError />}
      onError={error => {
        console.error('Error caught by boundary:', error);
        if (ENV.USE_SENTRY) {
          Sentry.captureException(error);
        }
      }}
      onReset={() => window.location.reload()}
      resetKeys={[]}
    >
      <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
        <LanguageProvider defaultLanguage="en" storageKey="vite-ui-language">
          <GoogleOAuthProvider clientId={ENV.GOOGLE_CLIENT_ID}>
            {msalInstance ? (
              <MsalProvider instance={msalInstance}>
                <RouterProvider router={router} />
              </MsalProvider>
            ) : (
              <RouterProvider router={router} />
            )}
            <Toaster />
            <SonnerToaster />
          </GoogleOAuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(<App />);
